import { gql, useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { GET_SELLER_STORE } from "../../api/seller-store";
import { notify } from "../../../../utils/toast";
import { Swiper, SwiperSlide } from "swiper/react";
import { useRef } from "react";
import "swiper/swiper-bundle.css";
import { HiArrowLeft, HiArrowRight } from "react-icons/hi";
import { Swiper as SwiperType } from "swiper/types";
import { GET_RESERVE_EXCEPTIONS_BY_MONTH } from "./reserve-calendar";
import { useReserveException } from "../context/reserve-exception-provider";
import { useNavigate } from "react-router-dom";

const TOGGLE_RESERVE_EXCEPTION = gql`
  mutation ToggleReserveException(
    $dateString: String!
    $isReservable: Boolean!
  ) {
    toggleReserveException(
      dateString: $dateString
      isReservable: $isReservable
    ) {
      ok
      error
    }
  }
`;
export const GET_PRE_ORDER_SCHEDULES = gql`
  query preOrderSchedules {
    preOrderSchedules {
      dateString
      isReservable
      reserveTime
      unprocessedCount
      isEnabled
    }
  }
`;

const PreOrderWeeklySchedule = () => {
  const navigate = useNavigate();
  const { setDateStatuses } = useReserveException();
  const { preOrderSchedules, setPreOrderSchedules } = useReserveException();
  const { data: preOrderSchedulesData, refetch } = useQuery(
    GET_PRE_ORDER_SCHEDULES,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        setPreOrderSchedules(data.preOrderSchedules);
      },
    }
  );

  const { data: storeData } = useQuery(GET_SELLER_STORE, {
    fetchPolicy: "network-only",
  });

  const [toggleReserveException] = useMutation(TOGGLE_RESERVE_EXCEPTION);
  const [selectedDateString, setSelectedDateString] = useState<string | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false); // 모달 상태 추가

  const handleToggleReserveException = (dateString: string) => {
    setSelectedDateString(dateString);
    setIsModalOpen(true); // 모달 열기
  };

  const confirmToggle = () => {
    const index = preOrderSchedulesData?.preOrderSchedules.findIndex(
      (schedule: any) => schedule.dateString === selectedDateString
    );

    toggleReserveException({
      variables: {
        dateString: selectedDateString,
        isReservable: !preOrderSchedules[index].isReservable,
      },
      onCompleted: () => {
        refetch();
        setPreOrderSchedules((prev) => {
          const newPreOrderSchedules = [...prev];
          newPreOrderSchedules[index] = {
            ...newPreOrderSchedules[index],
            isReservable: !newPreOrderSchedules[index].isReservable,
          };
          return newPreOrderSchedules;
        });

        // ---------------- 타 컴포넌트의 상태 업데이트 ----------------
        // 캘린더 표기 업데이트 // reserve-calendar.tsx 참고
        // from context
        setDateStatuses((prev) => {
          const newDateStatuses = [...prev];
          const targetIndex = newDateStatuses.findIndex(
            (status) => status.dateString === selectedDateString
          );
          newDateStatuses[targetIndex] = {
            ...newDateStatuses[targetIndex],
            isReservable: !newDateStatuses[targetIndex].isReservable,
          };
          return newDateStatuses;
        });
        // ---------------- 타 컴포넌트의 상태 업데이트 ----------------

        notify("적용되었습니다 👋");
      },

      // ---------------- 타 컴포넌트의 캐시 업데이트 ----------------
      // 캘린더 캐시 업데이트 // reserve-calendar.tsx 참고
      update: (cache, { data }) => {
        const existingData = cache.readQuery({
          query: GET_RESERVE_EXCEPTIONS_BY_MONTH,
        }) as { getReserveExceptions: any[] };

        if (existingData) {
          let updatedExceptions = [...existingData.getReserveExceptions];

          if (!preOrderSchedules[index].isReservable) {
            // isReservable이 true일 때 해당 객체 삭제
            updatedExceptions = updatedExceptions.filter(
              (exception: any) => exception.dateString !== selectedDateString
            );
          } else {
            // isReservable이 false일 때 새로운 객체 생성
            const newException = {
              id: Date.now(),
              dateString: selectedDateString,
              start: "", // 필요한 값으로 초기화
              end: "", // 필요한 값으로 초기화
              isReservable: false,
              note: "", // 필요한 값으로 초기화
            };
            updatedExceptions.push(newException);
          }

          cache.writeQuery({
            query: GET_RESERVE_EXCEPTIONS_BY_MONTH,
            data: { getReserveExceptions: updatedExceptions },
          });
        }
      },
      // ---------------- 타 컴포넌트의 캐시 업데이트 ----------------
    });
    setIsModalOpen(false); // 모달 닫기
  };

  const swiperRef = useRef<SwiperType | null>(null); // Swiper 인스턴스를 위한 ref 생성

  return (
    <>
      <div className="w-full">
        <Swiper
          spaceBetween={10}
          initialSlide={0}
          style={{
            paddingLeft: "2rem",
            paddingRight: "2rem",
          }}
          centeredSlides={true}
          onSwiper={(swiper: SwiperType) => (swiperRef.current = swiper)}
        >
          {preOrderSchedulesData?.preOrderSchedules.map(
            (schedule: any, index: number) => (
              <SwiperSlide key={schedule.dateString}>
                <div className="flex flex-col items-center border-2 border-gray-300 rounded-lg shadow-lg">
                  {/* header */}
                  <div className="flex items-center justify-evenly w-full mt-2">
                    {index === 0 ? (
                      <div className="w-6 h-6 p-2" />
                    ) : (
                      <div
                        onClick={() => {
                          if (swiperRef.current) {
                            swiperRef.current.slidePrev(); // 이전 슬라이드로 이동
                          }
                        }}
                        className="p-2 bg-gray-300 rounded-full text-gray-500 cursor-pointer"
                      >
                        <HiArrowLeft className="w-3 h-3" />
                      </div>
                    )}
                    <h2 className="text-2xl font-bold text-center text-gray-800">
                      {index === 0
                        ? "오늘"
                        : index === 1
                        ? "내일"
                        : `${
                            new Date(schedule.dateString).getMonth() + 1
                          }/${new Date(
                            schedule.dateString
                          ).getDate()} ${new Date(
                            schedule.dateString
                          ).toLocaleDateString("ko-KR", {
                            weekday: "long",
                          })}`}
                    </h2>
                    {index ===
                    preOrderSchedulesData?.preOrderSchedules.length - 1 ? (
                      <div className="w-4 h-4 p-2" />
                    ) : (
                      <div
                        onClick={() => {
                          swiperRef.current?.slideNext(); // 다음 슬라이드로 이동
                        }}
                        className="p-2 bg-gray-300 rounded-full text-gray-500 cursor-pointer"
                      >
                        <HiArrowRight className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                  {/* box */}
                  {schedule.isEnabled ? (
                    <div className="mt-2 border border-gray-300 rounded-lg mx-2 cursor-pointer w-full h-52 bg-white flex flex-col justify-center">
                      {/* upper box (toggle button) */}
                      <div
                        className={`flex justify-center items-center bg-blue-400 text-xl font-bold rounded-t-lg p-2`}
                      >
                        <div className="flex items-center">
                          <span className="text-white mr-4">
                            예약판매 on/off
                          </span>
                          <button
                            className={`flex items-center justify-center w-16 h-8 rounded-full transition-colors duration-300 ${
                              schedule.isReservable
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                            onClick={() =>
                              handleToggleReserveException(schedule.dateString)
                            }
                          >
                            <span
                              className={`w-12 h-8 bg-white rounded-full transform transition-transform duration-300 ${
                                schedule.isReservable
                                  ? "translate-x-[12px]"
                                  : "translate-x-[-12px]"
                              }`}
                            >
                              {schedule.isReservable ? "ON" : "OFF"}
                            </span>
                          </button>
                        </div>
                      </div>
                      {/* lower box (reserve time) */}
                      <div className="text-lg space-y-2 break-keep py-4 pl-2 pr-1 h-full">
                        <div className="flex font-semibold items-center">
                          <span className="w-1/3">픽업시간</span>
                          <span className="w-2/3 text-blue-500 font-extrabold text-2xl">
                            {schedule.reserveTime}
                          </span>
                        </div>
                        {index === 0 || index === 1 ? (
                          <div className="flex font-semibold items-center">
                            <span className="w-1/3">확인필요 주문</span>
                            <span className="w-2/3 text-blue-500 font-extrabold text-2xl">
                              {schedule.unprocessedCount}
                              <span className="text-gray-500 text-lg pl-1">
                                개
                              </span>
                            </span>
                          </div>
                        ) : (
                          <div className="flex font-semibold items-center">
                            <span className="w-1/3"></span>
                            <span className="w-2/3 text-blue-500 font-extrabold text-2xl"></span>
                          </div>
                        )}
                        <div className="flex font-semibold">
                          <span className="mt-1 w-1/3">담을 음식가격</span>
                          <span className="w-2/3 text-blue-500 font-extrabold text-2xl">
                            {storeData?.getSellerStore?.pack?.price.toLocaleString()}
                            원 이상 필수{" "}
                            <div className="text-gray-500 text-lg">
                              / 팩 1개당
                            </div>
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-lg mx-2 cursor-pointer w-full bg-white h-52 flex flex-col items-center justify-center">
                      <div className="text-2xl font-bold">
                        주문 받지 않는 날 👋
                      </div>
                      <div
                        onClick={() =>
                          navigate("/boss/pre-order/pickup-time-update")
                        }
                        className="text-blue-500 text-lg underline mt-2"
                      >
                        변경하기
                      </div>
                    </div>
                  )}
                </div>
              </SwiperSlide>
            )
          )}
        </Swiper>
      </div>

      <div className="w-full flex flex-col items-center">
        {/* 모달 추가 */}
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="bg-white rounded p-6">
              <div className="break-keep font-bold text-3xl p-8 text-center">
                {preOrderSchedules[
                  preOrderSchedulesData?.preOrderSchedules.findIndex(
                    (schedule: any) =>
                      schedule.dateString === selectedDateString
                  )
                ].isReservable
                  ? "예약을 더 이상 받지 않습니다."
                  : "예약을 받습니다."}
              </div>
              <div className="break-keep font-semibold text-xl pb-4 text-gray-400 text-center">
                {preOrderSchedules[
                  preOrderSchedulesData?.preOrderSchedules.findIndex(
                    (schedule: any) =>
                      schedule.dateString === selectedDateString
                  )
                ].unprocessedCount > 0 &&
                  preOrderSchedules[
                    preOrderSchedulesData?.preOrderSchedules.findIndex(
                      (schedule: any) =>
                        schedule.dateString === selectedDateString
                    )
                  ].isReservable &&
                  "현재 들어온 주문이 취소되진 않습니다."}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-300 px-4 py-2 rounded"
                >
                  취소
                </button>
                <button
                  onClick={confirmToggle}
                  className="ml-2 bg-blue-500 text-white px-8 py-2 rounded"
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PreOrderWeeklySchedule;
